import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtUSD, fmtPct } from "@/lib/api";
import { PageHeader, Card, Empty } from "@/components/Bits";
import { Link } from "react-router-dom";
import { Upload, Sparkles, MessageSquare, Film, Pin } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine,
} from "recharts";
import MorningMessage from "@/components/MorningMessage";
import ProfitGoals from "@/components/ProfitGoals";
import StreakBadge from "@/components/StreakBadge";
import PeriodTabs from "@/components/PeriodTabs";

// Compact tooltip for the equity curve. Purple accent lines match the chart.
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div
      className="px-3 py-2 text-xs pointer-events-none shadow-lg space-y-0.5"
      style={{
        background: "var(--surface2)",
        border: "1px solid rgba(168,85,247,0.25)",
        color: "var(--fg)",
        borderRadius: "10px",
        fontFamily: "var(--font-mono)",
      }}
    >
      <div style={{ color: "var(--fg-2)", fontWeight: 600 }}>{fmtTooltipDate(label)}</div>
      <div style={{ color: "var(--accent)" }}>
        Equity: <span style={{ fontVariantNumeric: "tabular-nums" }}>${(payload[0].value || 0).toFixed(2)}</span>
      </div>
      {typeof row.pnl === "number" && (
        <div style={{ color: row.pnl >= 0 ? "var(--green)" : "var(--loss)" }}>
          Day P&L: ${row.pnl.toFixed(2)}
        </div>
      )}
    </div>
  );
};

const fmtChartDate = (v) => {
  if (typeof v !== "string" || v.length < 10) return v;
  // Per-trade curves (single-day windows on the Dashboard) send full ISO
  // timestamps like "2026-07-13T09:22:00Z". Detect the time part and render
  // as HH:MM so the x-axis shows the intraday build-up.
  if (v.length > 10 && v.includes("T")) {
    const dt = new Date(v);
    if (!isNaN(dt.getTime())) {
      return dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    }
  }
  const [y, m, d] = v.slice(0, 10).split("-");
  const dt = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  if (isNaN(dt.getTime())) return v;
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
};

const fmtTooltipDate = (v) => {
  if (typeof v !== "string" || v.length < 10) return v;
  // Per-trade tooltip: show weekday + full time (e.g. "Mon 09:22 AM").
  if (v.length > 10 && v.includes("T")) {
    const dt = new Date(v);
    if (!isNaN(dt.getTime())) {
      return dt.toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" });
    }
  }
  const [y, m, d] = v.slice(0, 10).split("-");
  const dt = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  if (isNaN(dt.getTime())) return v;
  return dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
};

// Big stat tile. Purple eyebrow, oversized number, subtle sub-label.
const StatTile = ({ label, value, tone = "neutral", sub, testid }) => {
  const color =
    tone === "profit" ? "var(--green)" :
    tone === "loss" ? "var(--loss)" :
    tone === "accent" ? "var(--accent)" : "var(--fg)";
  return (
    <div
      className="p-6 relative overflow-hidden group transition-all hover:-translate-y-0.5"
      data-testid={testid}
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "16px",
        backdropFilter: "blur(12px)",
      }}
    >
      {/* Subtle purple corner glow on hover */}
      <div
        aria-hidden
        className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-2xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(168,85,247,0.25), transparent 70%)" }}
      />
      <div className="text-[11px] font-medium tracking-wider uppercase relative" style={{ color: "var(--fg-3)" }}>{label}</div>
      <div className="text-4xl font-semibold mt-2 relative" style={{
        color,
        fontFamily: "var(--font-heading)",
        fontVariantNumeric: "tabular-nums",
        letterSpacing: "-0.03em",
      }}>{value}</div>
      {sub && <div className="text-xs mt-1.5 relative" style={{ color: "var(--fg-3)" }}>{sub}</div>}
    </div>
  );
};

// Purple-gradient equity curve — the visual hero of the dashboard.
const EquityChart = ({ data }) => (
  <div
    className="p-6 relative overflow-hidden"
    style={{
      background: "linear-gradient(135deg, rgba(168,85,247,0.05) 0%, rgba(99,102,241,0.03) 40%, rgba(20,20,28,0.75) 100%)",
      border: "1px solid rgba(168,85,247,0.12)",
      borderRadius: "20px",
      backdropFilter: "blur(16px)",
      boxShadow: "0 20px 60px -20px rgba(168,85,247,0.15)",
    }}
  >
    {/* Ambient purple glow blobs — kept subtle */}
    <div
      aria-hidden
      className="absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(168,85,247,0.5), transparent 70%)" }}
    />
    <div
      aria-hidden
      className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full opacity-10 blur-3xl pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(99,102,241,0.4), transparent 70%)" }}
    />
    <div className="flex items-baseline justify-between mb-6 relative">
      <div>
        <div className="text-[11px] font-medium tracking-wider uppercase" style={{ color: "var(--accent)" }}>Equity Curve</div>
        <div className="text-lg font-semibold mt-1" style={{ color: "var(--fg)", fontFamily: "var(--font-heading)", letterSpacing: "-0.02em" }}>Cumulative P&L over time</div>
      </div>
    </div>
    <div className="h-80 relative" data-testid="dashboard-equity-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="equity-purple" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#A855F7" stopOpacity={0.40} />
              <stop offset="60%" stopColor="#6366F1" stopOpacity={0.12} />
              <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="equity-line" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#A855F7" />
              <stop offset="50%" stopColor="#C084FC" />
              <stop offset="100%" stopColor="#8B5CF6" />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="date"
            stroke="rgba(250,250,250,0.3)"
            fontSize={10}
            tick={{ fontFamily: "var(--font-mono)" }}
            tickFormatter={fmtChartDate}
            minTickGap={32}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            stroke="rgba(250,250,250,0.3)"
            fontSize={10}
            tick={{ fontFamily: "var(--font-mono)" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
          />
          <Tooltip
            content={ChartTooltip}
            cursor={{ stroke: "#A855F7", strokeWidth: 1, strokeDasharray: "3 4", opacity: 0.6 }}
            isAnimationActive={false}
          />
          <ReferenceLine y={0} stroke="rgba(255,255,255,0.15)" strokeDasharray="3 3" />
          <Area
            type="monotone"
            dataKey="equity"
            stroke="url(#equity-line)"
            strokeWidth={2.5}
            fill="url(#equity-purple)"
            isAnimationActive={false}
            activeDot={{
              r: 5,
              fill: "#A855F7",
              stroke: "#FAFAFA",
              strokeWidth: 2,
              filter: "drop-shadow(0 0 8px rgba(168,85,247,0.7))",
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

// The Dashboard's "favorite clip" slot. Only renders when the user has pinned
// one from the Library (see `is_dashboard_clip` PATCH endpoint). Kept intentionally
// low-key — sits at the bottom, out of the way, but looks cool if used.
const FavoriteClipCard = () => {
  const { data: clip } = useQuery({
    queryKey: ["dashboard-clip"],
    queryFn: async () => (await api.get("/video/clips/dashboard")).data,
    staleTime: 60_000,
  });

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  if (!clip) {
    return (
      <div
        className="p-6 flex items-center justify-between relative overflow-hidden"
        data-testid="dashboard-clip-empty"
        style={{
          background: "var(--surface)",
          border: "1px dashed var(--border)",
          borderRadius: "16px",
        }}
      >
        <div className="flex items-center gap-4">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center"
            style={{ background: "rgba(168,85,247,0.12)", border: "1px solid rgba(168,85,247,0.3)" }}
          >
            <Film className="h-5 w-5" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
          </div>
          <div>
            <div className="text-sm font-medium" style={{ color: "var(--fg)" }}>Pin your favorite trade replay here</div>
            <div className="text-xs mt-0.5" style={{ color: "var(--fg-3)" }}>
              Show off your best setup right on the dashboard. Head to <span style={{ color: "var(--accent)" }}>Library</span>, hit the pin icon on any clip.
            </div>
          </div>
        </div>
        <Link
          to="/library"
          data-testid="dashboard-clip-goto-library"
          className="px-4 py-2 text-xs font-medium transition-colors"
          style={{ background: "var(--accent-soft)", color: "var(--accent)", border: "1px solid rgba(168,85,247,0.35)", borderRadius: "10px" }}
        >
          Open Library
        </Link>
      </div>
    );
  }

  const streamUrl = `${API_BASE}/api/video/clips/${clip.id}/stream?token=${encodeURIComponent(localStorage.getItem("edge_token") || "")}`;
  const pnlColor = (clip.pnl || 0) >= 0 ? "var(--green)" : "var(--loss)";

  return (
    <div
      data-testid="dashboard-clip"
      className="overflow-hidden relative"
      style={{
        background: "linear-gradient(135deg, rgba(168,85,247,0.10) 0%, rgba(20,20,28,0.7) 60%)",
        border: "1px solid rgba(168,85,247,0.28)",
        borderRadius: "20px",
        boxShadow: "0 24px 60px -20px rgba(168,85,247,0.35)",
      }}
    >
      <div className="flex items-center justify-between px-6 pt-5 pb-3">
        <div className="flex items-center gap-2.5">
          <Pin className="h-4 w-4" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
          <div>
            <div className="text-[11px] tracking-wider uppercase" style={{ color: "var(--accent)" }}>Pinned Clip</div>
            <div className="text-sm font-medium mt-0.5" style={{ color: "var(--fg)" }}>
              {clip.symbol} <span style={{ color: "var(--fg-3)" }}>·</span>{" "}
              <span style={{ color: pnlColor, fontVariantNumeric: "tabular-nums", fontFamily: "var(--font-mono)" }}>{fmtUSD(clip.pnl)}</span>
            </div>
          </div>
        </div>
        <Link
          to={`/library`}
          className="text-[10px] tracking-widest uppercase transition-colors"
          style={{ color: "var(--fg-3)" }}
        >
          Manage →
        </Link>
      </div>
      <div className="px-6 pb-6">
        <video
          src={streamUrl}
          controls
          preload="auto"
          playsInline
          className="w-full rounded-xl bg-black"
          style={{ aspectRatio: "16 / 9", maxHeight: 460 }}
          data-testid="dashboard-clip-player"
        />
        <div className="mt-3 flex items-center justify-between text-[11px] font-mono-num" style={{ color: "var(--fg-3)" }}>
          <span>{clip.date} · {clip.side?.toUpperCase() || ""} · {Math.round(clip.duration_seconds || 0)}s</span>
          {clip.notes && <span className="truncate max-w-md">{clip.notes}</span>}
        </div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const [period, setPeriod] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [showMorning, setShowMorning] = useState(false);
  const [netPnl, setNetPnl] = useState(true);

  // When on a preset (day/week/…/all) we send `?period=...`. When the user
  // picks a specific window via the calendar popover we send `?from=&to=` so
  // the same endpoint handles both.
  const rangeParams = period === "custom"
    ? { from: customFrom, to: customTo }
    : { period };
  const rangeKey = period === "custom" ? `custom:${customFrom}:${customTo}` : period;

  const { data: morningMeta } = useQuery({
    queryKey: ["morning-message"],
    queryFn: async () => (await api.get("/recap/morning-message")).data,
  });
  const hasMorningMessage = Boolean(morningMeta && (morningMeta.message_to_future_self || morningMeta.edge_brief));

  const { data, isLoading } = useQuery({
    queryKey: ["stats", rangeKey],
    queryFn: async () => (await api.get("/stats/overview", { params: rangeParams })).data,
    enabled: period !== "custom" || (!!customFrom && !!customTo),
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 15_000,
  });

  if (isLoading) return <div className="p-8 text-xs font-mono-num text-zinc-500">LOADING METRICS…</div>;

  const noData = !data || data.total_trades === 0;
  const netTotalPnl = netPnl ? (data?.total_pnl || 0) : (data?.gross_pnl ?? data?.total_pnl ?? 0);
  const hasFees = (data?.total_fees || 0) > 0;

  return (
    <div>
      <PageHeader
        eyebrow="Dashboard"
        title="Performance overview"
        action={
          <div className="flex gap-2 items-center">
            <StreakBadge />
            {hasMorningMessage && !showMorning && (
              <button
                onClick={() => setShowMorning(true)}
                data-testid="open-morning-btn"
                className="flex items-center gap-2 px-4 py-2 text-sm transition-colors"
                style={{ background: "var(--accent-soft)", color: "var(--accent)", border: "1px solid rgba(168,85,247,0.35)", borderRadius: "12px" }}
              >
                <MessageSquare className="h-4 w-4" strokeWidth={1.5} /> Open Coach&apos;s message
              </button>
            )}
            <Link to="/import" data-testid="header-import-btn"
              className="flex items-center gap-2 px-4 py-2 text-sm transition-colors"
              style={{ background: "var(--surface2)", color: "var(--fg)", border: "1px solid var(--border)", borderRadius: "12px" }}>
              <Upload className="h-4 w-4" strokeWidth={1.5} /> Import CSV
            </Link>
            <Link to="/coach" data-testid="header-coach-btn"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors"
              style={{ background: "var(--accent-gradient)", color: "#FAFAFA", borderRadius: "12px", boxShadow: "var(--accent-glow)" }}>
              <Sparkles className="h-4 w-4" strokeWidth={1.5} /> Ask Coach
            </Link>
          </div>
        }
      />

      <MorningMessage open={showMorning} onClose={() => setShowMorning(false)} />

      <div className="px-8 pb-16 space-y-8">
        {/* 1 · GOALS at the top */}
        <ProfitGoals />

        {/* Range selector — subtle, sits between goals and the chart.
            Clicking the left calendar icon opens the custom-range popover. */}
        <div className="flex items-center justify-between">
          <PeriodTabs
            value={period}
            onChange={setPeriod}
            customFrom={customFrom}
            customTo={customTo}
            onCustomChange={({ from, to }) => {
              setCustomFrom(from);
              setCustomTo(to);
              if (from && to) setPeriod("custom");
            }}
          />
          {hasFees && (
            <button
              type="button"
              onClick={() => setNetPnl((v) => !v)}
              data-testid="pnl-net-toggle"
              className="px-3 py-1.5 text-[10px] tracking-widest transition-colors"
              style={{
                background: netPnl ? "rgba(168,85,247,0.14)" : "transparent",
                border: `1px solid ${netPnl ? "rgba(168,85,247,0.45)" : "var(--border)"}`,
                color: netPnl ? "var(--accent)" : "var(--fg-3)",
                borderRadius: "8px",
              }}
              title="Toggle between net (after fees) and gross (before fees)"
            >
              {netPnl ? "NET · AFTER FEES" : "GROSS · BEFORE FEES"}
            </button>
          )}
        </div>

        {noData ? (
          <Empty
            title="No trades logged yet"
            sub="Import your trade CSV from any broker (or log one manually) to unlock metrics, equity curve, and AI coaching."
            action={
              <Link to="/import" className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-colors"
                style={{ background: "var(--accent-gradient)", color: "#FAFAFA", borderRadius: "12px", boxShadow: "var(--accent-glow)" }}>
                <Upload className="h-4 w-4" strokeWidth={1.5} /> Import your first CSV
              </Link>
            }
          />
        ) : (
          <>
            {/* 2 · HERO CHART — purple-gradient equity curve */}
            <EquityChart data={data.equity_curve} />

            {/* 3 · The four stats that matter */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatTile
                label="Net P&L"
                value={fmtUSD(netTotalPnl)}
                tone={netTotalPnl >= 0 ? "profit" : "loss"}
                sub={hasFees ? `${fmtUSD(data.total_fees)} in fees` : `${data.total_trades} trades`}
                testid="metric-net-pnl"
              />
              <StatTile
                label="Win Rate"
                value={fmtPct(data.win_rate)}
                tone="accent"
                sub={`${data.total_trades} trades total`}
                testid="metric-win-rate"
              />
              <StatTile
                label="Avg Win"
                value={fmtUSD(data.avg_win)}
                tone="profit"
                sub={data.avg_win_cents_per_share ? `${data.avg_win_cents_per_share.toFixed(1)}¢ / share` : null}
                testid="metric-avg-win"
              />
              <StatTile
                label="Avg Loss"
                value={fmtUSD(data.avg_loss)}
                tone="loss"
                sub={data.avg_loss_cents_per_share ? `${data.avg_loss_cents_per_share.toFixed(1)}¢ / share` : null}
                testid="metric-avg-loss"
              />
            </div>

            {/* 4 · Deep-dive link */}
            <div className="flex items-center justify-center pt-2">
              <Link
                to="/analytics"
                data-testid="dashboard-goto-analytics"
                className="inline-flex items-center gap-2 px-4 py-2 text-xs tracking-wider uppercase transition-colors"
                style={{ color: "var(--fg-3)", border: "1px solid var(--border)", borderRadius: "999px" }}
              >
                Deep-dive stats
                <span style={{ color: "var(--accent)" }}>→</span>
              </Link>
            </div>

            {/* 5 · Favorite clip (out of the way, cool if used) */}
            <div className="pt-4">
              <FavoriteClipCard />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
